import { Button } from "@/components/ui/button";
import { Menu, Linkedin } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import aiLabbetLogo from "@/assets/ai-labbet-white.png";
import { trackEvent } from "@/lib/analytics";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActiveLink = (href: string) => {
    if (href === "/" && location.pathname === "/") return true;
    if (href !== "/" && location.pathname === href) return true;
    if (href.includes("#") && location.pathname === "/" && href.startsWith("/")) return true;
    return false;
  };

  const menuItems = [
    { label: "Home", href: "/" },
    { label: "About us", href: "/about" },
    { label: "AI-skilling", href: "/ai-skilling" },
    { label: "Services", href: "/services" },
    { label: "News", href: "/news" },
    { label: "Contact", href: "/contact" },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b transition-all duration-300 ${
      isScrolled 
        ? "border-border shadow-[0_4px_12px_rgba(0,0,0,0.3)]" 
        : "border-border/50"
    }`}>
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={aiLabbetLogo} 
              alt="AI-labbet - Business value through responsible AI" 
              className="h-10 lg:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-10">
            {menuItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className={`text-sm font-medium transition-colors ${
                  isActiveLink(item.href)
                    ? "text-primary font-semibold border-b-2 border-primary pb-1"
                    : "text-foreground/80 hover:text-foreground"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Button variant="secondary" size="sm" asChild>
              <a
                href="https://www.linkedin.com/company/ai-labbet/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center"
                onClick={() =>
                  trackEvent("outbound_click", { destination: "linkedin", location: "header_desktop" })
                }
              >
                <Linkedin className="w-4 h-4 mr-2" />
                Follow us
              </a>
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-foreground"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <Menu size={24} />
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 flex flex-col gap-4">
            {menuItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className={`text-sm font-medium transition-colors ${
                  isActiveLink(item.href)
                    ? "text-primary font-semibold"
                    : "text-foreground/80 hover:text-foreground"
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <Button variant="secondary" size="sm" className="w-full" asChild>
              <a
                href="https://www.linkedin.com/company/ai-labbet/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center"
                onClick={() =>
                  trackEvent("outbound_click", { destination: "linkedin", location: "header_mobile" })
                }
              >
                <Linkedin className="w-4 h-4 mr-2" />
                Follow us
              </a>
            </Button>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
